import {
  createNavigatorFactory,
  StackRouter,
  useNavigationBuilder,
  type DefaultNavigatorOptions,
  type ParamListBase,
  type StackActionHelpers,
  type StackNavigationState,
  type StackRouterOptions,
} from '@react-navigation/native'
import { router, withLayoutContext } from 'expo-router'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { PanResponder, Platform, View } from 'react-native'
import { Appbar } from 'react-native-paper'
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  type SharedValue,
} from 'react-native-reanimated'
import {
  StyleSheet,
  UnistylesBreakpointContainerProvider,
  useUnistyles,
} from 'react-native-unistyles'

import UniIconButton from '@/components/unistyles/UniIconButton'
import { useBreakpoint } from '@/hooks/useBreakpoint'

const ANIMATION_DURATION = 350
const ANIMATION_EASING = Easing.bezier(0.4, 0, 0.2, 1)
const DEFAULT_DETAIL_RATIO = 0.5
const MIN_DETAIL_RATIO = 0.25
const MAX_DETAIL_RATIO = 0.75
const DIVIDER_HIT_WIDTH = 12

export type SplitViewNavigationEventMap = Record<string, never>

export type SplitViewNavigationOptions = {
  headerShown?: boolean
  title?: string
  headerRight?: (props: { canGoBack: boolean }) => React.ReactNode
  contentStyle?: Record<string, unknown>
}

type SplitViewNavigatorProps = DefaultNavigatorOptions<
  ParamListBase,
  // @ts-expect-error - workaround for React Navigation v7 type params
  StackNavigationState<ParamListBase>,
  SplitViewNavigationOptions,
  SplitViewNavigationEventMap,
  any,
  any
> &
  StackRouterOptions

// ─── Resize Divider ──────────────────────────────────────────────────────────

interface ResizeDividerProps {
  detailRatio: SharedValue<number>
  containerWidthRef: React.MutableRefObject<number>
  onDragStateChange: (dragging: boolean) => void
}

const DOUBLE_TAP_MS = 300

const ResizeDivider = ({
  detailRatio,
  containerWidthRef,
  onDragStateChange,
}: ResizeDividerProps) => {
  const [isHovered, setIsHovered] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const isActive = isHovered || isDragging
  const initialRatio = useRef(DEFAULT_DETAIL_RATIO)
  const lastReleaseAt = useRef(0)
  const lastGestureWasTap = useRef(false)

  const setDragging = (value: boolean) => {
    setIsDragging(value)
    onDragStateChange(value)
  }

  const resetToDefault = () => {
    detailRatio.value = withTiming(DEFAULT_DETAIL_RATIO, {
      duration: ANIMATION_DURATION,
      easing: ANIMATION_EASING,
    })
  }

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: (_, gesture) =>
        Math.abs(gesture.dx) > Math.abs(gesture.dy),
      onPanResponderGrant: () => {
        const now = Date.now()
        if (
          now - lastReleaseAt.current < DOUBLE_TAP_MS &&
          lastGestureWasTap.current
        ) {
          resetToDefault()
          lastGestureWasTap.current = false
          return
        }
        initialRatio.current = detailRatio.value
        setDragging(true)
      },
      onPanResponderMove: (_, gesture) => {
        const width = containerWidthRef.current
        if (width <= 0) return
        const ratioChange = -gesture.dx / width
        detailRatio.value = Math.max(
          MIN_DETAIL_RATIO,
          Math.min(MAX_DETAIL_RATIO, initialRatio.current + ratioChange)
        )
      },
      onPanResponderRelease: () => {
        const moved = Math.abs(detailRatio.value - initialRatio.current) > 0.01
        lastGestureWasTap.current = !moved
        if (!moved) {
          lastReleaseAt.current = Date.now()
        }
        if (Platform.OS === 'web') {
          // Defer re-enabling pointer events so any deferred click events are blocked
          requestAnimationFrame(() => {
            requestAnimationFrame(() => setDragging(false))
          })
        } else {
          setDragging(false)
        }
      },
      onPanResponderTerminate: () => setDragging(false),
    })
  ).current

  return (
    <View
      accessibilityHint="Drag horizontally to resize the master and detail panels. Double-tap to reset to default size."
      accessibilityLabel="Resize panels"
      accessibilityRole="adjustable"
      {...panResponder.panHandlers}
      {...Platform.select({
        web: {
          onDoubleClick: resetToDefault,
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => {
            if (!isDragging) setIsHovered(false)
          },
        },
      })}
      style={[
        styles.dividerHitArea,
        Platform.OS === 'web' && { cursor: 'col-resize' as never },
      ]}
    >
      <View
        style={isActive ? styles.dividerLineActive : styles.dividerLineInactive}
      />
    </View>
  )
}

// ─── Navigator ───────────────────────────────────────────────────────────────

const SplitViewNavigatorComponent = ({
  id,
  children,
  screenListeners,
  screenOptions,
  initialRouteName,
}: SplitViewNavigatorProps) => {
  const { isAtLeastLg } = useBreakpoint()

  const isXlRef = useRef(false)
  isXlRef.current = isAtLeastLg

  // When the split view is active, PUSH or NAVIGATE actions targeting the base
  // route (e.g. pushing "/fosters" which resolves through the (tabs) group)
  // would normally push a duplicate or pop the detail routes. Instead we
  // directly update the child tab state and preserve any detail routes so the
  // right panel stays open.
  const splitViewRouter = useMemo(
    () => (options: StackRouterOptions) => {
      const stack = StackRouter(options)
      return {
        ...stack,
        getStateForRouteFocus(
          state: StackNavigationState<ParamListBase>,
          key: string
        ) {
          const index = state.routes.findIndex((r) => r.key === key)
          if (isXlRef.current && index === 0 && state.routes.length > 1) {
            return state
          }
          return stack.getStateForRouteFocus(state, key)
        },
        getStateForAction(
          state: StackNavigationState<ParamListBase>,
          action: any,
          actionOptions: any
        ) {
          if (
            isXlRef.current &&
            (action.type === 'PUSH' || action.type === 'NAVIGATE') &&
            state.routes[0] &&
            action.payload?.name === state.routes[0].name
          ) {
            const baseRoute = state.routes[0]
            const targetScreen = action.payload.params?.screen
            const hasNestedParams = action.payload.params?.params != null

            // When there are nested params (e.g. pushing to fosters/filters), we must
            // use the full navigation flow so child navigators (BottomSheet) receive
            // the action and push the nested screen. The fast path only switches tabs
            // and does not propagate nested navigation.
            if (targetScreen && baseRoute.state && !hasNestedParams) {
              const childState = baseRoute.state as any
              const targetIndex = childState.routes?.findIndex(
                (r: any) => r.name === targetScreen
              )

              if (targetIndex !== undefined && targetIndex >= 0) {
                const updatedBaseRoute = {
                  ...baseRoute,
                  state: { ...childState, index: targetIndex },
                }
                return {
                  ...state,
                  routes: [updatedBaseRoute, ...state.routes.slice(1)],
                } as typeof state
              }
            }

            // Child state not yet initialised — process on a single-route
            // slice so NAVIGATE finds the base route as the current route,
            // then graft the result back with detail routes preserved.
            const singleRouteState = {
              ...state,
              routes: [state.routes[0]],
              index: 0,
            } as typeof state

            const asNavigate =
              action.type === 'PUSH' ? { ...action, type: 'NAVIGATE' } : action
            const navigated = stack.getStateForAction(
              singleRouteState,
              asNavigate,
              actionOptions
            )

            if (!navigated || navigated === singleRouteState) {
              return state
            }

            return {
              ...state,
              routes: [
                navigated.routes[navigated.routes.length - 1],
                ...state.routes.slice(1),
              ],
            } as typeof state
          }
          return stack.getStateForAction(state, action, actionOptions)
        },
      }
    },
    []
  )

  const { state, descriptors, NavigationContent } = useNavigationBuilder<
    StackNavigationState<ParamListBase>,
    StackRouterOptions,
    StackActionHelpers<ParamListBase>,
    SplitViewNavigationOptions,
    SplitViewNavigationEventMap
  >(splitViewRouter, {
    id,
    children,
    screenListeners,
    screenOptions,
    initialRouteName,
  })

  const { theme } = useUnistyles()
  const containerWidthRef = useRef(0)
  const [isResizeDragging, setIsResizeDragging] = useState(false)

  const baseRoute = state.routes[0]
  const detailRoute =
    state.routes.length > 1 ? state.routes[state.routes.length - 1] : null
  const shouldSplit = isAtLeastLg && detailRoute !== null

  const progress = useSharedValue(shouldSplit ? 1 : 0)
  const detailRatio = useSharedValue(DEFAULT_DETAIL_RATIO)

  useEffect(() => {
    progress.value = withTiming(shouldSplit ? 1 : 0, {
      duration: ANIMATION_DURATION,
      easing: ANIMATION_EASING,
    })
  }, [shouldSplit, progress])

  const masterAnimatedStyle = useAnimatedStyle(() => ({
    flex: interpolate(progress.value, [0, 1], [1, 1 - detailRatio.value]),
  }))

  const detailAnimatedStyle = useAnimatedStyle(() => ({
    flex: interpolate(progress.value, [0, 1], [0.001, detailRatio.value]),
    opacity: interpolate(progress.value, [0, 0.3, 1], [0, 0.5, 1]),
    transform: [{ translateX: interpolate(progress.value, [0, 1], [80, 0]) }],
  }))

  if (!baseRoute) {
    return <NavigationContent>{null}</NavigationContent>
  }

  // ── XL+ screens: split view ─────────────────────────────────────────────
  if (isAtLeastLg) {
    const detailDescriptor = detailRoute
      ? descriptors[detailRoute.key]
      : undefined
    const detailOptions = detailDescriptor?.options ?? {}

    return (
      <NavigationContent>
        <View
          onLayout={(e) => {
            containerWidthRef.current = e.nativeEvent.layout.width
          }}
          style={styles.splitRow}
        >
          {/* Master panel (tabs) */}
          <Animated.View
            pointerEvents={isResizeDragging ? 'none' : 'auto'}
            style={[masterAnimatedStyle, styles.overflowHidden]}
          >
            {descriptors[baseRoute.key].render()}
          </Animated.View>

          {/* Divider + Detail panel */}
          {detailRoute && detailDescriptor && (
            <>
              <ResizeDivider
                containerWidthRef={containerWidthRef}
                detailRatio={detailRatio}
                onDragStateChange={setIsResizeDragging}
              />

              <Animated.View
                pointerEvents={isResizeDragging ? 'none' : 'auto'}
                style={[detailAnimatedStyle, styles.detailPanel]}
              >
                <UnistylesBreakpointContainerProvider
                  style={{ flex: 1, minHeight: 0 }}
                >
                  <Appbar.Header elevated={false} style={styles.appbarHeader}>
                    <UniIconButton
                      icon="close"
                      iconColor={theme.colors.onSurfaceVariant}
                      onPress={() => router.back()}
                      testID="split-view-close"
                    />
                    <Appbar.Content
                      style={styles.flex1}
                      title={detailOptions.title ?? ''}
                      titleMaxFontSizeMultiplier={1.5}
                    />
                    {detailOptions.headerRight?.({ canGoBack: true })}
                  </Appbar.Header>

                  <View style={styles.flex1}>{detailDescriptor.render()}</View>
                </UnistylesBreakpointContainerProvider>
              </Animated.View>
            </>
          )}
        </View>
      </NavigationContent>
    )
  }

  // ── Small screens: standard stack ───────────────────────────────────────
  return (
    <NavigationContent>
      {state.routes.map((route, i) => {
        const isFocused = state.index === i
        const descriptor = descriptors[route.key]
        const { options } = descriptor

        if (!isFocused && i > 0) return null

        return (
          <View
            key={route.key}
            style={[styles.stackRoute, !isFocused && styles.stackRouteHidden]}
          >
            {isFocused && i > 0 && options.headerShown !== false && (
              <Appbar.Header elevated={false} style={styles.appbarHeader}>
                <UniIconButton
                  icon="arrow-left"
                  iconColor={theme.colors.primary}
                  onPress={() => router.back()}
                  testID="back-icon-button"
                />
                <View style={styles.stackHeaderRow}>
                  <Appbar.Content
                    style={styles.stackHeaderContent}
                    title={options.title || route.name}
                    titleMaxFontSizeMultiplier={1.5}
                  />
                </View>
                {options.headerRight?.({ canGoBack: true })}
              </Appbar.Header>
            )}
            {descriptor.render()}
          </View>
        )
      })}
    </NavigationContent>
  )
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create((theme) => ({
  appbarHeader: {
    backgroundColor: theme.colors.surface,
    borderBottomColor: theme.colors.cardBorder,
    borderBottomWidth: 1,
    paddingRight: 16,
  },
  detailPanel: {
    backgroundColor: theme.colors.background,
    overflow: 'hidden' as const,
  },
  dividerHitArea: {
    alignItems: 'center' as const,
    alignSelf: 'stretch' as const,
    justifyContent: 'center' as const,
    userSelect: 'none' as const,
    WebkitUserSelect: 'none' as const,
    width: DIVIDER_HIT_WIDTH,
  },
  dividerLineActive: {
    backgroundColor: theme.colors.primary,
    borderRadius: 2,
    height: '100%',
    width: 4,
  },
  dividerLineInactive: {
    backgroundColor: theme.colors.cardBorder,
    borderRadius: 0,
    height: '100%',
    width: 1,
  },
  flex1: {
    flex: 1,
    minHeight: 0,
  },
  overflowHidden: {
    overflow: 'hidden' as const,
  },
  splitRow: {
    flex: 1,
    flexDirection: 'row' as const,
  },
  stackHeaderContent: {
    flex: Platform.OS === 'web' ? 0.4 : 1,
  },
  stackHeaderRow: {
    flex: 1,
    flexDirection: 'row' as const,
  },
  stackRoute: {
    backgroundColor: theme.colors.background,
    flex: 1,
    minHeight: 0,
  },
  stackRouteHidden: {
    display: 'none' as const,
  },
}))

// ─── Factory + Expo Router integration ───────────────────────────────────────

const createSplitView = createNavigatorFactory(SplitViewNavigatorComponent)
const { Navigator } = createSplitView()

export const SplitView = withLayoutContext<
  SplitViewNavigationOptions,
  typeof Navigator,
  any,
  any
>(Navigator)
